import React, { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  CreditCard, 
  User, 
  Settings, 
  LogOut,
  Plus,
  Menu,
  X
} from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui';

/**
 * @component PanelLayout
 * @description Layout principal del panel de usuario con sidebar y header
 */

interface PanelLayoutProps {
  children: ReactNode;
}

const PanelLayout: React.FC<PanelLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/panel', icon: Home },
    { name: 'Mis Pulseras', href: '/panel/webids', icon: CreditCard },
    { name: 'Perfil', href: '/dashboard/settings', icon: User },
    { name: 'Configuración', href: '/dashboard/settings', icon: Settings },
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const isCurrentPath = (path: string) => {
    if (path === '/panel') {
      return location.pathname === '/panel';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo y navegación móvil */}
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                {sidebarOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
              
              <Link to="/panel" className="flex items-center ml-4 md:ml-0">
                <div className="flex-shrink-0">
                  <img
                    className="h-8 w-auto"
                    src="/logo-nfcores.svg"
                    alt="NFCores"
                  />
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900">
                  Panel
                </span>
              </Link>
            </div>

            {/* Botón vincular pulsera y usuario */}
            <div className="flex items-center gap-4">
              {location.pathname.startsWith('/panel/webids') && (
                <Link
                  to="/activar"
                  className="hidden sm:inline-flex items-center px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Vincular nueva pulsera
                </Link>
              )}
              
              {/* Usuario */}
              <div className="flex items-center gap-3">
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.displayName || user?.email}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.role === 'admin' ? 'Administrador' : 'Usuario'}
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  {user?.photoURL ? (
                    <img
                      className="h-8 w-8 rounded-full"
                      src={user.photoURL}
                      alt={user.displayName || 'Usuario'}
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary-600" />
                    </div>
                  )}
                  
                  <button
                    onClick={handleLogout}
                    className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-md"
                    title="Cerrar sesión"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <nav className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 fixed md:static inset-y-0 left-0 z-50
          w-64 bg-white shadow-lg transform transition-transform duration-200 ease-in-out
          md:shadow-none md:border-r md:border-gray-200
        `}>
          <div className="flex flex-col h-full pt-5 pb-4">
            <div className="flex-1 px-3 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const current = isCurrentPath(item.href);
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md
                      ${current
                        ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <Icon
                      className={`
                        mr-3 h-5 w-5 flex-shrink-0
                        ${current ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'}
                      `}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Botón vincular pulsera móvil */}
            <div className="px-3 pb-4 md:hidden">
              <Link
                to="/activar"
                className="w-full inline-flex items-center justify-center px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                onClick={() => setSidebarOpen(false)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Vincular nueva pulsera
              </Link>
            </div>
          </div>
        </nav>

        {/* Overlay para móvil */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Contenido principal */}
        <main className="flex-1 min-w-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PanelLayout;
